import React, { useState, useCallback, useEffect } from 'react';
import { ParametersLayout } from './ParametersLayout';
import { ui } from '../../../config/uiSizes';

interface PropertyUpdateRow {
  id: string;
  propertyName: string;
  newValue: string;
  [key: string]: unknown;
}

interface PropertyBatchUpdateParametersProps {
  parameters?: Record<string, unknown>;
  onParametersChange?: (parameters: Record<string, unknown>) => void;
}

export const PropertyBatchUpdateParameters: React.FC<PropertyBatchUpdateParametersProps> = ({
  parameters,
  onParametersChange
}) => {
  const [rows, setRows] = useState<PropertyUpdateRow[]>(() => {
    const propertyUpdates = parameters?.['propertyUpdates'];

    if (propertyUpdates && Array.isArray(propertyUpdates)) {
      if (propertyUpdates.length > 0) {
        return propertyUpdates.map(
          (update: { PropertyName?: string; NewValue?: string }, index) => ({
            id: (index + 1).toString(),
            propertyName: update.PropertyName || '',
            newValue: update.NewValue || ''
          })
        );
      }
    }

    return [{ id: '1', propertyName: '', newValue: '' }];
  });

  const [onlyIfExists, setOnlyIfExists] = useState<boolean>(
    parameters?.['onlyIfExists'] !== undefined ? (parameters['onlyIfExists'] as boolean) : false
  );

  useEffect(() => {
    if (onParametersChange) {
      const propertyUpdatesArray = rows
        .filter((row) => row.propertyName && row.newValue)
        .map((row) => ({
          PropertyName: row.propertyName,
          NewValue: row.newValue
        }));

      onParametersChange({
        propertyUpdates: propertyUpdatesArray,
        onlyIfExists
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, onlyIfExists]);

  const handleAddRow = useCallback(() => {
    const newRow: PropertyUpdateRow = {
      id: Date.now().toString(),
      propertyName: '',
      newValue: ''
    };
    setRows((prev) => [...prev, newRow]);
  }, []);

  const handleRemoveRow = useCallback((id: string | number) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  }, []);

  const handleUpdateRow = useCallback(
    (id: string | number, field: 'propertyName' | 'newValue', value: string) => {
      setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    },
    []
  );

  const renderRow = useCallback(
    (row: PropertyUpdateRow, index: number, onRemove: () => void) => (
      <div className={`flex items-end ${ui.gap.md}`}>
        <div className="flex-1">
          {index === 0 && (
            <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
              Property Name
            </label>
          )}
          <input
            type="text"
            value={row.propertyName}
            onChange={(e) => handleUpdateRow(row.id, 'propertyName', e.target.value)}
            placeholder="state"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
        </div>

        <div className="flex items-center pb-2">
          <svg
            className={`${ui.svg.sm} text-gray-400 dark:text-gray-500`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </div>

        <div className="flex-1">
          {index === 0 && (
            <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
              New Value
            </label>
          )}
          <input
            type="text"
            value={row.newValue}
            onChange={(e) => handleUpdateRow(row.id, 'newValue', e.target.value)}
            placeholder="active"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
        </div>

        <button
          type="button"
          onClick={onRemove}
          className={`flex items-center gap-2 ${ui.input.base} ${ui.text.label} text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors mb-0.5`}
        >
          <svg className={`${ui.svg.sm}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
          Remove
        </button>
      </div>
    ),
    [handleUpdateRow]
  );

  return (
    <div className="space-y-4">
      <ParametersLayout<PropertyUpdateRow>
        title="Property Batch Update"
        description="Update multiple properties in a single transformation."
        addButtonLabel="Add property update"
        rows={rows}
        onAddRow={handleAddRow}
        onRemoveRow={handleRemoveRow}
        renderRow={renderRow}
      />

      <div className={`border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 ${ui.page.cardPadding}`}>
        <div className={`flex items-center ${ui.gap.sm}`}>
          <input
            type="checkbox"
            id="onlyIfExists"
            checked={onlyIfExists}
            onChange={(e) => setOnlyIfExists(e.target.checked)}
            className={`${ui.input.base} text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-400`}
          />
          <label htmlFor="onlyIfExists" className={`${ui.text.label} text-gray-700 dark:text-gray-300`}>
            Only update properties that already exist
          </label>
        </div>
      </div>
    </div>
  );
};

export default PropertyBatchUpdateParameters;
